import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import {
    ClaimRuleActivity,
    CoderRulesDashboardEndpoint,
    CoderRulesDashboardStore,
    CoderRuleSummary,
    FAILED_TRANSMISSION_STATUSES,
    getClaimTransmissionStatus,
    getRuleKey,
    SUCCESS_TRANSMISSION_STATUSES,
} from '../../stores/coder-rules-dashboard/coder-rules-dashboard.store';
import { Banner, BaseButton, BaseInput, IBaseButtonProps, Notice, Spinner } from '../base';
import { downloadCsvBlob } from '../data-table/components/table-export/csv-utils';
import { buildCoderRuleClaimsCsv, buildCoderRuleClaimsCsvFilename } from './coder-rules-csv.util';
import { DashboardShell } from './coder-rules-dashboard.styles';

/**
 * `BaseButton`'s unstyled variant spreads its props onto the <button>, but its type does not list the
 * ARIA state a selectable row or a toggle pill needs — so this adds the two, and nothing else.
 */
const StateButton = BaseButton as unknown as React.ComponentType<
    IBaseButtonProps & { 'aria-current'?: boolean; 'aria-pressed'?: boolean }
>;

interface CoderRulesDashboardProps {
    /** Load on mount when nothing is cached. */
    autoLoad?: boolean;
    endpoint?: CoderRulesDashboardEndpoint;
    /** Stamped on rules the API returns without an owner, and used to link claims. */
    userId?: string;
    userName?: string;
}

function formatDate(value?: string) {
    if (!value) {
        return 'Unknown';
    }

    const parsed = new Date(value);

    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    }).format(parsed);
}

function formatPercent(value: number | null, isLoadingTransmissions = false) {
    if (value !== null) {
        return `${Math.round(value)}%`;
    }

    return isLoadingTransmissions ? 'Loading transmissions' : 'No transmissions';
}

function buildClaimSupportUrl(userId: string, claimId: string) {
    const currentOrigin = typeof window === 'undefined' ? '' : window.location.origin;

    return `${currentOrigin}/${encodeURIComponent(userId)}/claims/${encodeURIComponent(claimId)}`;
}

function statusClassName(status: string) {
    const normalizedStatus = status.toUpperCase();

    if (SUCCESS_TRANSMISSION_STATUSES.has(normalizedStatus)) {
        return 'status-pill status-success';
    }

    if (FAILED_TRANSMISSION_STATUSES.has(normalizedStatus)) {
        return 'status-pill status-failed';
    }

    if (normalizedStatus === 'NO_STATUS') {
        return 'status-pill status-missing';
    }

    return 'status-pill status-submitted';
}

function formatTransmissionStatusLabel(status: string) {
    return status === 'NO_STATUS' ? 'MISSING' : status;
}

/** Exports every claim matching the status filter — all pages, not just the 25 currently shown. */
function downloadCoderRuleClaimsCsv(rule: CoderRuleSummary, claims: ClaimRuleActivity[]) {
    const rows = claims.map(claim => {
        const claimUserId = claim.userId || rule.ownerId;

        return {
            ruleNumber: rule.ruleNumber,
            ruleDescription: rule.description,
            claimId: claim.claimId,
            runDate: claim.runDate || claim.codingCreated,
            transmissionStatus: formatTransmissionStatusLabel(getClaimTransmissionStatus(claim)),
            transmissionDate: claim.latestTransmission?.created,
            claimUrl: claimUserId ? buildClaimSupportUrl(claimUserId, claim.claimId) : null,
        };
    });

    downloadCsvBlob(buildCoderRuleClaimsCsv(rows), buildCoderRuleClaimsCsvFilename(rule.ruleNumber));
}

const CoderRulesDashboard = observer(function CoderRulesDashboard({
    autoLoad = false,
    endpoint = 'support',
    userId,
    userName,
}: CoderRulesDashboardProps) {
    const [store] = useState(() => new CoderRulesDashboardStore({ endpoint, userId, userName }));

    // Every read, filter and page lives in the store; the screen only renders it and forwards clicks.
    useEffect(() => {
        store.configure({ endpoint, userId, userName });
        void store.start(autoLoad);
    }, [autoLoad, endpoint, store, userId, userName]);

    const { globalRules, lastLoadedAt, loading: isLoadingDashboard, rules: ruleSummaries, selectedRule } = store;
    const selectedRuleClaims = store.selectedRuleClaims;

    return (
        <DashboardShell>
            <div className="page">
                <header className="page-head">
                    <div className="page-head-text">
                        <h1>Coder Rules</h1>
                        <p>
                            Automated coding edits applied before transmission. Each rule shows its status, the claims
                            it touched, and how those claims fared downstream.
                        </p>
                    </div>
                    <div className="page-head-actions">
                        <BaseButton
                            disabled={isLoadingDashboard}
                            onClick={() => {
                                void store.load();
                            }}
                            size="compact"
                            type="button"
                            variant="primary"
                        >
                            {isLoadingDashboard
                                ? 'Loading rules'
                                : lastLoadedAt
                                  ? 'Refresh dashboard'
                                  : 'Load dashboard'}
                        </BaseButton>
                    </div>
                </header>

                <Notice tone="info">
                    Claim rejections or denials do not necessarily mean a coder rule is underperforming. A claim can be
                    rejected or denied for reasons unrelated to the rule, so these metrics are directional indicators of
                    rule health.
                </Notice>

                {store.error ? (
                    <Banner tone="danger" title={store.error} />
                ) : store.progress ? (
                    <Banner
                        tone="info"
                        title={
                            <>
                                {isLoadingDashboard && <Spinner size="16px" />} {store.progress}
                            </>
                        }
                    />
                ) : null}

                <section className="stat-row" aria-label="Coder rules summary">
                    <div className="stat">
                        <span className="stat-label">Coder rules</span>
                        <span className="stat-value">{ruleSummaries.length}</span>
                        <span className="stat-note">{store.activeRulesCount} active</span>
                    </div>
                    <div className="stat">
                        <span className="stat-label">Claims</span>
                        <span className="stat-value">{store.matchedClaimCount}</span>
                        <span className="stat-note">Unique claims in returned rows</span>
                    </div>
                    <div className="stat">
                        <span className="stat-label">Overall success rate</span>
                        <span className="stat-value">
                            {formatPercent(store.overallSuccessRate, isLoadingDashboard)}
                        </span>
                        <span className="stat-note">
                            {store.overallTransmittedCount} claims with success/failure statuses
                        </span>
                    </div>
                    <div className="stat">
                        <span className="stat-label">Last loaded</span>
                        <span className="stat-value date">{formatDate(lastLoadedAt?.toISOString())}</span>
                        <span className="stat-note">Latest transmission fields from the API</span>
                    </div>
                </section>

                <p className="methodology-note">
                    Rules load from the coder-rules endpoint first; claim and transmission metrics fill in from the
                    dashboard endpoint afterward, so figures briefly render blank. Success compares successful statuses
                    against failed; pending and missing transmissions are excluded.
                </p>

                <div className="split">
                    <aside className="rules-pane" aria-label="All rules">
                        <div className="rules-pane-head">
                            <div className="rules-pane-title">
                                <span>All rules</span>
                                <span>{ruleSummaries.length} loaded</span>
                            </div>
                            <BaseInput
                                aria-label="Filter rules"
                                compact={true}
                                onChange={event => store.setRuleFilter(event.target.value)}
                                placeholder="Filter by rule name or number"
                                type="text"
                                value={store.ruleFilter}
                            />
                        </div>
                        <div className="rules-list">
                            {ruleSummaries.length === 0 ? (
                                <div className="rules-empty">No coder rules loaded</div>
                            ) : store.filteredRules.length === 0 ? (
                                <div className="rules-empty">No rules match your filter</div>
                            ) : (
                                store.filteredRules.map(rule => {
                                    const ruleKey = getRuleKey(rule);
                                    const isSelected = selectedRule ? getRuleKey(selectedRule) === ruleKey : false;
                                    const ruleLabel =
                                        typeof rule.ruleNumber === 'number' ? `Rule ${rule.ruleNumber}` : 'Rule';
                                    const statusLabel =
                                        rule.isActive === true
                                            ? 'ACTIVE'
                                            : rule.isActive === false
                                              ? 'INACTIVE'
                                              : 'UNKNOWN';

                                    return (
                                        <StateButton
                                            aria-current={isSelected}
                                            className={isSelected ? 'rule-row selected' : 'rule-row'}
                                            key={ruleKey}
                                            onClick={() => store.selectRule(ruleKey)}
                                            type="button"
                                            variant="unstyled"
                                        >
                                            <div className="rule-row-top">
                                                <span className="rule-row-label">{ruleLabel}</span>
                                                <span
                                                    className={
                                                        rule.isActive === true
                                                            ? 'status-pill status-completed'
                                                            : 'status-pill'
                                                    }
                                                >
                                                    {statusLabel}
                                                </span>
                                                <span className="rule-row-claims">{rule.claims.length} claims</span>
                                            </div>
                                            <span className="rule-row-desc">
                                                {rule.description || 'No description'}
                                            </span>
                                            <span className="rule-row-meta">
                                                Latest run {formatDate(rule.latestRunDate)} ·{' '}
                                                {formatPercent(rule.successRate, isLoadingDashboard)}
                                                {rule.failedCount > 0 ? (
                                                    <span className="rule-row-failed">
                                                        {' '}
                                                        · {rule.failedCount} failed
                                                    </span>
                                                ) : null}
                                            </span>
                                        </StateButton>
                                    );
                                })
                            )}
                        </div>
                    </aside>

                    <section className="detail-pane" aria-label="Rule detail">
                        {selectedRule && selectedRuleClaims ? (
                            (() => {
                                const {
                                    filtered: allFilteredClaims,
                                    remaining,
                                    ruleKey,
                                    selectedStatuses: selectedClaimStatuses,
                                    statusOptions: claimStatusOptions,
                                    visible: filteredClaims,
                                } = selectedRuleClaims;
                                const ruleLabel =
                                    typeof selectedRule.ruleNumber === 'number'
                                        ? `Rule ${selectedRule.ruleNumber}`
                                        : 'Rule';
                                const statusLabel =
                                    selectedRule.isActive === true
                                        ? 'ACTIVE'
                                        : selectedRule.isActive === false
                                          ? 'INACTIVE'
                                          : 'UNKNOWN';
                                const selectedClaimStatusSet = new Set(selectedClaimStatuses);
                                const rateClass =
                                    selectedRule.successRate === null
                                        ? ''
                                        : selectedRule.successRate >= 80
                                          ? ' good'
                                          : selectedRule.successRate >= 50
                                            ? ''
                                            : ' bad';

                                return (
                                    <>
                                        <div className="detail-head">
                                            <div className="detail-head-text">
                                                <div className="detail-badges">
                                                    <span className="detail-eyebrow">{ruleLabel}</span>
                                                    <span
                                                        className={
                                                            selectedRule.isActive === true
                                                                ? 'status-pill status-completed'
                                                                : 'status-pill'
                                                        }
                                                    >
                                                        {statusLabel}
                                                    </span>
                                                    {selectedRule.ruleKey ? (
                                                        <span className="detail-slug">{selectedRule.ruleKey}</span>
                                                    ) : null}
                                                </div>
                                                <h2>{selectedRule.description || 'No description'}</h2>
                                                {selectedRule.ownerName ? <p>Owner {selectedRule.ownerName}</p> : null}
                                            </div>
                                        </div>

                                        <div className="metric-strip">
                                            <div className="metric">
                                                <span className="metric-label">Latest run</span>
                                                <span className="metric-value">
                                                    {formatDate(selectedRule.latestRunDate)}
                                                </span>
                                                <span className="metric-note">
                                                    {selectedRule.created
                                                        ? `Created ${formatDate(selectedRule.created)}`
                                                        : 'Most recent rule run'}
                                                </span>
                                            </div>
                                            <div className="metric">
                                                <span className="metric-label">Claims touched</span>
                                                <span className="metric-value">{selectedRule.claims.length}</span>
                                                <span className="metric-note">
                                                    {selectedRule.transmittedClaimCount} transmitted
                                                </span>
                                            </div>
                                            <div className="metric">
                                                <span className="metric-label">Success rate</span>
                                                <span className={`metric-value${rateClass}`}>
                                                    {formatPercent(selectedRule.successRate, isLoadingDashboard)}
                                                </span>
                                                <span className="metric-note">success vs. failed statuses</span>
                                            </div>
                                            <div className="metric">
                                                <span className="metric-label">Outcomes</span>
                                                <span className="metric-value">
                                                    {selectedRule.successCount} / {selectedRule.failedCount}
                                                </span>
                                                <span className="metric-note">
                                                    {selectedRule.pendingCount} pending ·{' '}
                                                    {selectedRule.missingTransmissionCount} missing
                                                </span>
                                            </div>
                                        </div>

                                        <div className="claims-block">
                                            <div className="claims-head-row">
                                                <div className="claims-head">
                                                    <strong>Claims touched by this rule</strong>
                                                    <span>
                                                        Latest transmission status per claim. Rejections and denials can
                                                        happen for reasons unrelated to the rule.
                                                    </span>
                                                </div>
                                                <BaseButton
                                                    disabled={allFilteredClaims.length === 0}
                                                    onClick={() =>
                                                        downloadCoderRuleClaimsCsv(selectedRule, allFilteredClaims)
                                                    }
                                                    size="compact"
                                                    type="button"
                                                    variant="primary"
                                                >
                                                    Export CSV
                                                </BaseButton>
                                            </div>

                                            {claimStatusOptions.length > 0 ? (
                                                <div
                                                    className="filter-bar"
                                                    role="group"
                                                    aria-label="Filter claims by latest transmission status"
                                                >
                                                    <span className="filter-bar-label">Status</span>
                                                    {claimStatusOptions.map(({ count, status }) => {
                                                        const isStatusSelected = selectedClaimStatusSet.has(status);

                                                        return (
                                                            <StateButton
                                                                aria-pressed={isStatusSelected}
                                                                className={`${statusClassName(status)} status-filter-pill${
                                                                    isStatusSelected ? ' selected' : ''
                                                                }`}
                                                                key={status}
                                                                onClick={() => store.toggleClaimStatus(ruleKey, status)}
                                                                type="button"
                                                                variant="unstyled"
                                                            >
                                                                {formatTransmissionStatusLabel(status)}
                                                                <span className="status-filter-count">{count}</span>
                                                            </StateButton>
                                                        );
                                                    })}
                                                    {selectedClaimStatuses.length > 0 ? (
                                                        <BaseButton
                                                            onClick={() => store.clearClaimStatuses(ruleKey)}
                                                            size="compact"
                                                            type="button"
                                                            variant="outlined"
                                                        >
                                                            Clear
                                                        </BaseButton>
                                                    ) : null}
                                                </div>
                                            ) : null}

                                            {selectedRule.claims.length === 0 ? (
                                                <div className="claims-empty">
                                                    This rule has not touched any claims yet.
                                                </div>
                                            ) : filteredClaims.length === 0 ? (
                                                <div className="claims-empty">
                                                    No claims match the selected statuses.
                                                </div>
                                            ) : (
                                                <div
                                                    className="claims-table"
                                                    role="table"
                                                    aria-label="Claims touched by this rule"
                                                >
                                                    <div className="claims-thead" role="row">
                                                        <span role="columnheader">Claim</span>
                                                        <span role="columnheader">Run date</span>
                                                        <span role="columnheader">Transmission</span>
                                                        <span role="columnheader">Created</span>
                                                        <span role="columnheader">Message</span>
                                                    </div>
                                                    {filteredClaims.map(claim => {
                                                        const status = getClaimTransmissionStatus(claim);
                                                        const claimUserId = claim.userId || selectedRule.ownerId;
                                                        const claimUrl = claimUserId
                                                            ? buildClaimSupportUrl(claimUserId, claim.claimId)
                                                            : null;

                                                        return (
                                                            <div className="claims-row" key={claim.claimId} role="row">
                                                                <span className="col-claim" role="cell">
                                                                    {claimUrl ? (
                                                                        <a
                                                                            href={claimUrl}
                                                                            rel="noreferrer"
                                                                            target="_blank"
                                                                        >
                                                                            {claim.claimId}
                                                                        </a>
                                                                    ) : (
                                                                        claim.claimId
                                                                    )}
                                                                </span>
                                                                <span role="cell">
                                                                    {formatDate(claim.runDate || claim.codingCreated)}
                                                                </span>
                                                                <span role="cell">
                                                                    <span className={statusClassName(status)}>
                                                                        {formatTransmissionStatusLabel(status)}
                                                                    </span>
                                                                </span>
                                                                <span role="cell">
                                                                    {formatDate(claim.latestTransmission?.created)}
                                                                </span>
                                                                <span className="col-msg" role="cell">
                                                                    {claim.latestTransmission?.message || 'No message'}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                            {remaining > 0 && (
                                                <div className="claims-more">
                                                    <BaseButton
                                                        onClick={() => store.showMoreClaims(ruleKey)}
                                                        size="compact"
                                                        type="button"
                                                        variant="outlined"
                                                    >
                                                        Show more ({remaining} remaining)
                                                    </BaseButton>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                );
                            })()
                        ) : (
                            <div className="detail-empty">
                                {ruleSummaries.length === 0
                                    ? 'Load the dashboard to see coder rules.'
                                    : 'Select a rule to see the claims it touched.'}
                            </div>
                        )}
                    </section>
                </div>

                {globalRules.status === 'error' && (
                    <section className="global-rules-section" aria-label="Global coder rules">
                        <div className="global-rules-head">
                            <strong>Global Rules</strong>
                        </div>
                        <Banner tone="warning" title="Global rules could not be loaded">
                            {globalRules.message}
                        </Banner>
                    </section>
                )}

                {globalRules.status === 'loaded' && (
                    <section className="global-rules-section" aria-label="Global coder rules">
                        <div className="global-rules-head">
                            <strong>Global Rules</strong>
                            <span>
                                Rules that run across all providers.{' '}
                                {globalRules.rules.filter(rule => rule.isActive).length} active of{' '}
                                {globalRules.rules.length} total.
                            </span>
                        </div>
                        {globalRules.rules.length === 0 ? (
                            <div className="claims-empty">No global rules.</div>
                        ) : (
                            <div className="global-rules-table" role="table" aria-label="Global coder rules">
                                <div className="global-rules-thead" role="row">
                                    <span role="columnheader">Rule</span>
                                    <span role="columnheader">Description</span>
                                    <span role="columnheader">Status</span>
                                </div>
                                {globalRules.rules.map((rule, index) => (
                                    <div className="global-rules-row" key={rule.ruleNumber} role="row">
                                        <span className="col-name" role="cell">
                                            Rule {index + 1}
                                        </span>
                                        <span role="cell">{rule.description || 'No description'}</span>
                                        <span role="cell">
                                            <span
                                                className={
                                                    rule.isActive ? 'status-pill status-completed' : 'status-pill'
                                                }
                                            >
                                                {rule.isActive ? 'ACTIVE' : 'INACTIVE'}
                                            </span>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                )}
            </div>
        </DashboardShell>
    );
});

export default CoderRulesDashboard;
